// src/store/slices/cancellationSlice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  checkCancellationEligibilityThunk,
  getCancellationQuoteThunk,
  processCancellationThunk,
  getCancellationStatusThunk,
  approveCancellationRequestThunk,
  getPendingCancellationsThunk,
  listRefundsThunk,
  getRefundStatusThunk,
  updateRefundStatusThunk,
  approveRefundThunk,
  completeRefundThunk,
  listCollectionsThunk,
  getCollectionStatusThunk,
  updateCollectionStatusThunk,
  recordCollectionPaymentThunk,
  waiveCollectionThunk,
  resolveCollectionThunk,
  getCancellationStatsThunk,
  getRefundStatsThunk,
  getCollectionStatsThunk,
  clearCancellationError,
  clearRefundData,
  clearCollectionData,
  clearStatsData,
  resetCancellationState,
} from "../../thunks/jewelleryPlan";

const initialState = {
  // Eligibility & Quote
  eligibility: null,
  quote: null,

  // Cancellation Status
  cancellationStatus: null,

  // Pending Cancellations
  pendingCancellations: [],
  pendingCancellationsPagination: {
    total: 0,
    page: 1,
    pages: 0,
  },

  // Refunds
  refunds: [],
  refundsPagination: {
    total: 0,
    page: 1,
    pages: 0,
  },
  currentRefund: null,

  // Collections
  collections: [],
  collectionsPagination: {
    total: 0,
    page: 1,
    pages: 0,
  },
  currentCollection: null,

  // Statistics
  cancellationStats: null,
  refundStats: null,
  collectionStats: null,

  // Loading states
  loading: false,
  eligibilityLoading: false,
  quoteLoading: false,
  processingLoading: false,
  statusLoading: false,
  pendingLoading: false,
  refundsLoading: false,
  refundActionLoading: false,
  collectionsLoading: false,
  collectionActionLoading: false,
  statsLoading: false,

  // Error states
  error: null,
  eligibilityError: null,
  quoteError: null,
  processingError: null,
  statusError: null,
  pendingError: null,
  refundError: null,
  collectionError: null,
  statsError: null,

  // Success flags
  cancellationProcessed: false,
  cancellationApproved: false, // ✅ NEW: Track approval success
  refundApproved: false,
  refundCompleted: false,
  collectionRecorded: false,
  collectionResolved: false,
  collectionWaived: false,
};

const cancellationSlice = createSlice({
  name: "cancellations",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ==================== CHECK ELIGIBILITY ====================
      .addCase(checkCancellationEligibilityThunk.pending, (state) => {
        state.eligibilityLoading = true;
        state.eligibilityError = null;
      })
      .addCase(checkCancellationEligibilityThunk.fulfilled, (state, action) => {
        state.eligibilityLoading = false;
        state.eligibility = action.payload.data;
      })
      .addCase(checkCancellationEligibilityThunk.rejected, (state, action) => {
        state.eligibilityLoading = false;
        state.eligibilityError = action.payload;
      })

      // ==================== GET QUOTE ====================
      .addCase(getCancellationQuoteThunk.pending, (state) => {
        state.quoteLoading = true;
        state.quoteError = null;
      })
      .addCase(getCancellationQuoteThunk.fulfilled, (state, action) => {
        state.quoteLoading = false;
        state.quote = action.payload.data;
      })
      .addCase(getCancellationQuoteThunk.rejected, (state, action) => {
        state.quoteLoading = false;
        state.quoteError = action.payload;
      })

      // ==================== PROCESS CANCELLATION ====================
      .addCase(processCancellationThunk.pending, (state) => {
        state.processingLoading = true;
        state.processingError = null;
        state.cancellationProcessed = false;
      })
      .addCase(processCancellationThunk.fulfilled, (state, action) => {
        state.processingLoading = false;
        state.cancellationStatus = action.payload.data;
        state.cancellationProcessed = true;
      })
      .addCase(processCancellationThunk.rejected, (state, action) => {
        state.processingLoading = false;
        state.processingError = action.payload;
        state.cancellationProcessed = false;
      })

      // ==================== GET CANCELLATION STATUS ====================
      .addCase(getCancellationStatusThunk.pending, (state) => {
        state.statusLoading = true;
        state.statusError = null;
      })
      .addCase(getCancellationStatusThunk.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.cancellationStatus = action.payload.data;
      })
      .addCase(getCancellationStatusThunk.rejected, (state, action) => {
        state.statusLoading = false;
        state.statusError = action.payload;
      })

      // ==================== GET PENDING CANCELLATIONS ====================
      .addCase(getPendingCancellationsThunk.pending, (state) => {
        state.pendingLoading = true;
        state.pendingError = null;
      })
      .addCase(getPendingCancellationsThunk.fulfilled, (state, action) => {
        state.pendingLoading = false;
        state.pendingCancellations = action.payload.data;
        state.pendingCancellationsPagination = action.payload.pagination || {
          total: action.payload.data.length,
          page: 1,
          pages: 1,
        };
      })
      .addCase(getPendingCancellationsThunk.rejected, (state, action) => {
        state.pendingLoading = false;
        state.pendingError = action.payload;
      })

      // ==================== APPROVE CANCELLATION REQUEST ====================
      .addCase(approveCancellationRequestThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.cancellationApproved = false; // ✅ Reset flag
      })
      .addCase(approveCancellationRequestThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.cancellationApproved = true; // ✅ Set flag
        // Remove from pending list if approved
        state.pendingCancellations = state.pendingCancellations.filter(
          (item) => item._id !== action.meta.arg.cancellationRequestId
        );
        // ✅ Update pagination count
        if (state.pendingCancellationsPagination.total > 0) {
          state.pendingCancellationsPagination.total -= 1;
        }
      })
      .addCase(approveCancellationRequestThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.cancellationApproved = false; // ✅ Reset flag
      })

      // ==================== LIST REFUNDS ====================
      .addCase(listRefundsThunk.pending, (state) => {
        state.refundsLoading = true;
        state.refundError = null;
      })
      .addCase(listRefundsThunk.fulfilled, (state, action) => {
        state.refundsLoading = false;
        state.refunds = action.payload.data;
        state.refundsPagination = action.payload.pagination || {
          total: action.payload.data.length,
          page: 1,
          pages: 1,
        };
      })
      .addCase(listRefundsThunk.rejected, (state, action) => {
        state.refundsLoading = false;
        state.refundError = action.payload;
      })

      // ==================== GET REFUND STATUS ====================
      .addCase(getRefundStatusThunk.pending, (state) => {
        state.refundsLoading = true;
        state.refundError = null;
      })
      .addCase(getRefundStatusThunk.fulfilled, (state, action) => {
        state.refundsLoading = false;
        state.currentRefund = action.payload.data;
      })
      .addCase(getRefundStatusThunk.rejected, (state, action) => {
        state.refundsLoading = false;
        state.refundError = action.payload;
      })

      // ==================== UPDATE REFUND STATUS ====================
      .addCase(updateRefundStatusThunk.pending, (state) => {
        state.refundActionLoading = true;
        state.refundError = null;
      })
      .addCase(updateRefundStatusThunk.fulfilled, (state, action) => {
        state.refundActionLoading = false;
        state.currentRefund = action.payload.data;
        // Update in list if present
        const index = state.refunds.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.refunds[index] = action.payload.data;
        }
      })
      .addCase(updateRefundStatusThunk.rejected, (state, action) => {
        state.refundActionLoading = false;
        state.refundError = action.payload;
      })

      // ==================== APPROVE REFUND ====================
      .addCase(approveRefundThunk.pending, (state) => {
        state.refundActionLoading = true;
        state.refundError = null;
        state.refundApproved = false;
      })
      .addCase(approveRefundThunk.fulfilled, (state, action) => {
        state.refundActionLoading = false;
        state.currentRefund = action.payload.data;
        state.refundApproved = true;
        // Update in list
        const index = state.refunds.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.refunds[index] = action.payload.data;
        }
      })
      .addCase(approveRefundThunk.rejected, (state, action) => {
        state.refundActionLoading = false;
        state.refundError = action.payload;
        state.refundApproved = false;
      })

      // ==================== COMPLETE REFUND ====================
      .addCase(completeRefundThunk.pending, (state) => {
        state.refundActionLoading = true;
        state.refundError = null;
        state.refundCompleted = false;
      })
      .addCase(completeRefundThunk.fulfilled, (state, action) => {
        state.refundActionLoading = false;
        state.currentRefund = action.payload.data;
        state.refundCompleted = true;
        // Update in list
        const index = state.refunds.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.refunds[index] = action.payload.data;
        }
      })
      .addCase(completeRefundThunk.rejected, (state, action) => {
        state.refundActionLoading = false;
        state.refundError = action.payload;
        state.refundCompleted = false;
      })

      // ==================== LIST COLLECTIONS ====================
      .addCase(listCollectionsThunk.pending, (state) => {
        state.collectionsLoading = true;
        state.collectionError = null;
      })
      .addCase(listCollectionsThunk.fulfilled, (state, action) => {
        state.collectionsLoading = false;
        state.collections = action.payload.data;
        state.collectionsPagination = action.payload.pagination || {
          total: action.payload.data.length,
          page: 1,
          pages: 1,
        };
      })
      .addCase(listCollectionsThunk.rejected, (state, action) => {
        state.collectionsLoading = false;
        state.collectionError = action.payload;
      })

      // ==================== GET COLLECTION STATUS ====================
      .addCase(getCollectionStatusThunk.pending, (state) => {
        state.collectionsLoading = true;
        state.collectionError = null;
      })
      .addCase(getCollectionStatusThunk.fulfilled, (state, action) => {
        state.collectionsLoading = false;
        state.currentCollection = action.payload.data;
      })
      .addCase(getCollectionStatusThunk.rejected, (state, action) => {
        state.collectionsLoading = false;
        state.collectionError = action.payload;
      })

      // ==================== UPDATE COLLECTION STATUS ====================
      .addCase(updateCollectionStatusThunk.pending, (state) => {
        state.collectionActionLoading = true;
        state.collectionError = null;
      })
      .addCase(updateCollectionStatusThunk.fulfilled, (state, action) => {
        state.collectionActionLoading = false;
        state.currentCollection = action.payload.data;
        // Update in list
        const index = state.collections.findIndex(
          (c) => c._id === action.payload.data._id
        );
        if (index !== -1) {
          state.collections[index] = action.payload.data;
        }
      })
      .addCase(updateCollectionStatusThunk.rejected, (state, action) => {
        state.collectionActionLoading = false;
        state.collectionError = action.payload;
      })

      // ==================== RECORD COLLECTION PAYMENT ====================
      .addCase(recordCollectionPaymentThunk.pending, (state) => {
        state.collectionActionLoading = true;
        state.collectionError = null;
        state.collectionRecorded = false;
      })
      .addCase(recordCollectionPaymentThunk.fulfilled, (state, action) => {
        state.collectionActionLoading = false;
        state.currentCollection = action.payload.data;
        state.collectionRecorded = true;
        // Update in list
        const index = state.collections.findIndex(
          (c) => c._id === action.payload.data._id
        );
        if (index !== -1) {
          state.collections[index] = action.payload.data;
        }
      })
      .addCase(recordCollectionPaymentThunk.rejected, (state, action) => {
        state.collectionActionLoading = false;
        state.collectionError = action.payload;
        state.collectionRecorded = false;
      })

      // ==================== WAIVE COLLECTION ====================
      .addCase(waiveCollectionThunk.pending, (state) => {
        state.collectionActionLoading = true;
        state.collectionError = null;
        state.collectionWaived = false;
      })
      .addCase(waiveCollectionThunk.fulfilled, (state, action) => {
        state.collectionActionLoading = false;
        state.currentCollection = action.payload.data;
        state.collectionWaived = true;
        // Update in list
        const index = state.collections.findIndex(
          (c) => c._id === action.payload.data._id
        );
        if (index !== -1) {
          state.collections[index] = action.payload.data;
        }
      })
      .addCase(waiveCollectionThunk.rejected, (state, action) => {
        state.collectionActionLoading = false;
        state.collectionError = action.payload;
        state.collectionWaived = false;
      })

      // ==================== RESOLVE COLLECTION ====================
      .addCase(resolveCollectionThunk.pending, (state) => {
        state.collectionActionLoading = true;
        state.collectionError = null;
        state.collectionResolved = false;
      })
      .addCase(resolveCollectionThunk.fulfilled, (state, action) => {
        state.collectionActionLoading = false;
        state.currentCollection = action.payload.data;
        state.collectionResolved = true;
        // Update in list
        const index = state.collections.findIndex(
          (c) => c._id === action.payload.data._id
        );
        if (index !== -1) {
          state.collections[index] = action.payload.data;
        }
      })
      .addCase(resolveCollectionThunk.rejected, (state, action) => {
        state.collectionActionLoading = false;
        state.collectionError = action.payload;
        state.collectionResolved = false;
      })

      // ==================== GET CANCELLATION STATS ====================
      .addCase(getCancellationStatsThunk.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(getCancellationStatsThunk.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.cancellationStats = action.payload.data;
      })
      .addCase(getCancellationStatsThunk.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
      })

      // ==================== GET REFUND STATS ====================
      .addCase(getRefundStatsThunk.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(getRefundStatsThunk.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.refundStats = action.payload.data;
      })
      .addCase(getRefundStatsThunk.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
      })

      // ==================== GET COLLECTION STATS ====================
      .addCase(getCollectionStatsThunk.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(getCollectionStatsThunk.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.collectionStats = action.payload.data;
      })
      .addCase(getCollectionStatsThunk.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
      })

      // ==================== UTILITY ACTIONS ====================
      .addCase(clearCancellationError, (state) => {
        state.error = null;
        state.eligibilityError = null;
        state.quoteError = null;
        state.processingError = null;
        state.statusError = null;
        state.pendingError = null;
        state.refundError = null;
        state.collectionError = null;
        state.statsError = null;
      })
      .addCase(clearRefundData, (state) => {
        state.currentRefund = null;
        state.refundError = null;
        state.refundApproved = false;
        state.refundCompleted = false;
      })
      .addCase(clearCollectionData, (state) => {
        state.currentCollection = null;
        state.collectionError = null;
        state.collectionRecorded = false;
        state.collectionResolved = false;
        state.collectionWaived = false;
      })
      .addCase(clearStatsData, (state) => {
        state.cancellationStats = null;
        state.refundStats = null;
        state.collectionStats = null;
        state.statsError = null;
      })
      .addCase(resetCancellationState, () => initialState);
  },
});

export default cancellationSlice.reducer;

// Selectors
export const selectCancellationEligibility = (state) =>
  state.jewelleryPlan.cancellation.eligibility;
export const selectCancellationQuote = (state) =>
  state.jewelleryPlan.cancellation.quote;
export const selectCancellationStatus = (state) =>
  state.jewelleryPlan.cancellation.cancellationStatus;
export const selectPendingCancellations = (state) =>
  state.jewelleryPlan.cancellation.pendingCancellations;
export const selectPendingCancellationsPagination = (state) =>
  state.jewelleryPlan.cancellation.pendingCancellationsPagination;

// Refund selectors
export const selectRefunds = (state) =>
  state.jewelleryPlan.cancellation.refunds;
export const selectRefundsPagination = (state) =>
  state.jewelleryPlan.cancellation.refundsPagination;
export const selectCurrentRefund = (state) =>
  state.jewelleryPlan.cancellation.currentRefund;
export const selectRefundApproved = (state) =>
  state.jewelleryPlan.cancellation.refundApproved;
export const selectRefundCompleted = (state) =>
  state.jewelleryPlan.cancellation.refundCompleted;

// Collection selectors
export const selectCollections = (state) =>
  state.jewelleryPlan.cancellation.collections;
export const selectCollectionsPagination = (state) =>
  state.jewelleryPlan.cancellation.collectionsPagination;
export const selectCurrentCollection = (state) =>
  state.jewelleryPlan.cancellation.currentCollection;
export const selectCollectionRecorded = (state) =>
  state.jewelleryPlan.cancellation.collectionRecorded;
export const selectCollectionResolved = (state) =>
  state.jewelleryPlan.cancellation.collectionResolved;
export const selectCollectionWaived = (state) =>
  state.jewelleryPlan.cancellation.collectionWaived;

// Stats selectors
export const selectCancellationStats = (state) =>
  state.jewelleryPlan.cancellation.cancellationStats;
export const selectRefundStats = (state) =>
  state.jewelleryPlan.cancellation.refundStats;
export const selectCollectionStats = (state) =>
  state.jewelleryPlan.cancellation.collectionStats;

// Loading selectors
export const selectEligibilityLoading = (state) =>
  state.jewelleryPlan.cancellation.eligibilityLoading;
export const selectQuoteLoading = (state) =>
  state.jewelleryPlan.cancellation.quoteLoading;
export const selectProcessingLoading = (state) =>
  state.jewelleryPlan.cancellation.processingLoading;
export const selectStatusLoading = (state) =>
  state.jewelleryPlan.cancellation.statusLoading;
export const selectPendingLoading = (state) =>
  state.jewelleryPlan.cancellation.pendingLoading;
export const selectRefundsLoading = (state) =>
  state.jewelleryPlan.cancellation.refundsLoading;
export const selectRefundActionLoading = (state) =>
  state.jewelleryPlan.cancellation.refundActionLoading;
export const selectCollectionsLoading = (state) =>
  state.jewelleryPlan.cancellation.collectionsLoading;
export const selectCollectionActionLoading = (state) =>
  state.jewelleryPlan.cancellation.collectionActionLoading;
export const selectStatsLoading = (state) =>
  state.jewelleryPlan.cancellation.statsLoading;

// Error selectors
export const selectCancellationError = (state) =>
  state.jewelleryPlan.cancellation.error;
export const selectEligibilityError = (state) =>
  state.jewelleryPlan.cancellation.eligibilityError;
export const selectQuoteError = (state) =>
  state.jewelleryPlan.cancellation.quoteError;
export const selectProcessingError = (state) =>
  state.jewelleryPlan.cancellation.processingError;
export const selectStatusError = (state) =>
  state.jewelleryPlan.cancellation.statusError;
export const selectPendingError = (state) =>
  state.jewelleryPlan.cancellation.pendingError;
export const selectRefundError = (state) =>
  state.jewelleryPlan.cancellation.refundError;
export const selectCollectionError = (state) =>
  state.jewelleryPlan.cancellation.collectionError;
export const selectStatsError = (state) =>
  state.jewelleryPlan.cancellation.statsError;

// Success flag selectors
export const selectCancellationProcessed = (state) =>
  state.jewelleryPlan.cancellation.cancellationProcessed;
export const selectCancellationApproved = (
  state // ✅ NEW
) => state.jewelleryPlan.cancellation.cancellationApproved;
